import { Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject, combineLatest, debounceTime, fromEvent, map, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { AppService } from './app.service';
import { FlagsState } from '../store/flags.state';
import { StatusBarService } from './status-bar.service';
import { AnalyticsService } from './analytics/analytics.service';
import { AnalyticsEventType } from './analytics/analytics.model';

export interface ProductTourStep {
  selector: string;
  text: string;
  buttonLabel: string;
  sproutyIcon?: string;
  skip?: boolean;
  illustration?: {
    url: string;
    align: 'default' | 'bottom';
  };
  align: ProductTourAlign;
  allowBack: boolean;
}

export interface ProductTourAlign {
  top?: string | 'auto';
  bottom?: string;
  left?: string;
  right?: string;
}
@Injectable({ providedIn: 'root' })
export class ProductTourService implements OnDestroy {
  private renderer: Renderer2;
  private destroyed$ = new Subject<void>();

  constructor(
    rendererFactory: RendererFactory2,
    private appService: AppService,
    private flagsState: FlagsState,
    private statusBarService: StatusBarService,
    private analyticsService: AnalyticsService,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

    this.currentStepIndex$.pipe(takeUntil(this.destroyed$)).subscribe(index => {
      this.productTourRunning$.next(index !== null);
    });
  }

  private stepsUpdated$ = new Subject<void>();
  private resizeSubscription?: Subscription;

  steps: Array<ProductTourStep> = [
    {
      selector: '',
      text: 'PWA_featureWalkthru_text_intro',
      buttonLabel: 'PWA_featureWalkthru_CTA_letsGo',
      sproutyIcon: 'hello.svg',
      align: {},
      skip: true,
      allowBack: false,
    },
    {
      selector: '',
      text: 'PWA_featureWalkthru_text_filters',
      buttonLabel: 'PWA_featureWalkthru_CTA_next',
      align: {},
      illustration: { url: 'books.svg', align: 'default' },
      allowBack: true,
    },
    {
      selector: '',
      text: 'PWA_featureWalkthru_text_categories',
      buttonLabel: 'PWA_featureWalkthru_CTA_next',
      align: {},
      illustration: { url: 'loop.svg', align: 'default' },
      allowBack: true,
    },
    {
      selector: '',
      text: 'PWA_featureWalkthru_text_read',
      buttonLabel: 'PWA_featureWalkthru_CTA_next',
      align: {},
      illustration: { url: 'binoculars.svg', align: 'default' },
      allowBack: true,
    },
    {
      selector: '',
      text: 'PWA_featureWalkthru_text_explore',
      buttonLabel: 'PWA_featureWalkthru_CTA_next',
      align: {},
      illustration: { url: 'books-flat.png', align: 'default' },
      allowBack: true,
    },
    {
      selector: '',
      text: 'PWA_featureWalkthru_text_saved',
      buttonLabel: 'PWA_featureWalkthru_CTA_next',
      align: {},
      illustration: { url: 'book-open.svg', align: 'default' },
      allowBack: true,
    },
    {
      selector: '',
      text: 'PWA_featureWalkthru_text_progress',
      buttonLabel: 'PWA_featureWalkthru_CTA_next',
      align: {},
      illustration: { url: 'trophy.png', align: 'default' },
      allowBack: true,
    },
    {
      selector: '',
      text: 'PWA_featureWalkthru_text_menu',
      buttonLabel: 'PWA_featureWalkthru_CTA_next',
      align: {},
      illustration: { url: 'person-question.png', align: 'bottom' },
      allowBack: true,
    },
    {
      selector: '',
      text: 'PWA_featureWalkthru_text_outro',
      buttonLabel: 'PWA_featureWalkthru_CTA_gotIt',
      sproutyIcon: 'happy.svg',
      align: {},
      allowBack: true,
    },
  ];

  currentStepIndex$: BehaviorSubject<number | null> = new BehaviorSubject<null | number>(null);
  productTourRunning$ = new BehaviorSubject<boolean>(false);
  currentStep$: Observable<ProductTourStep | null> = combineLatest([this.stepsUpdated$, this.currentStepIndex$]).pipe(
    map(([, index]) => (index != null ? this.steps[index] : null)),
  );

  async startTour(): Promise<void> {
    this.startListeningWidthChange();
    this.calculateStepsAlignment();
    this.currentStepIndex$.next(0);
    this.highlightElement(null, this.steps[Number(this.currentStepIndex$.value)].selector);
    this.analyticsService.sendEvent(AnalyticsEventType.WalkthroughStarted, null);
  }

  nextStep(): void {
    if (this.currentStepIndex$.value === null) {
      return;
    }

    if (this.currentStepIndex$.value < this.steps.length - 1) {
      this.highlightElement(this.steps[this.currentStepIndex$.value].selector, this.steps[this.currentStepIndex$.value + 1].selector);
      this.currentStepIndex$.next(this.currentStepIndex$.value + 1);
    } else {
      this.endTour();
    }

    this.analyticsService.sendEvent(AnalyticsEventType.WalkthroughStep, { direction: 'next', walkthroughStep: `${this.currentStepIndex$.value}/${this.steps.length}` });
  }

  previousStep(): void {
    if (this.currentStepIndex$.value === null || this.currentStepIndex$.value === 0) {
      return;
    }

    this.highlightElement(this.steps[this.currentStepIndex$.value].selector, this.steps[this.currentStepIndex$.value - 1].selector);
    this.currentStepIndex$.next(this.currentStepIndex$.value - 1);

    this.analyticsService.sendEvent(AnalyticsEventType.WalkthroughStep, { direction: 'previous', walkthroughStep: `${this.currentStepIndex$.value}/${this.steps.length}` });
  }

  endTour(): void {
    this.stopListeningWidthChange();
    const currentStep: number | null = this.currentStepIndex$.value;
    this.highlightElement(this.steps[Number(this.currentStepIndex$.value)].selector, null);
    this.currentStepIndex$.next(null);
    if (!this.flagsState.flags$.value.walkthroughCompletedOrDismissed) {
      void this.flagsState.set({ walkthroughCompletedOrDismissed: true });
    }

    // Send skipped / completion events
    if (currentStep === 0) {
      this.analyticsService.sendEvent(AnalyticsEventType.WalkthroughSkipped, null);
    } else {
      this.analyticsService.sendEvent(AnalyticsEventType.WalkthroughCompleted, null);
    }
  }

  private startListeningWidthChange(): void {
    this.resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(50))
      .subscribe(() => this.calculateStepsAlignment());
  }

  private stopListeningWidthChange(): void {
    this.resizeSubscription?.unsubscribe();
    this.resizeSubscription = undefined;
  }

  private highlightElement(previousSelector: string | null, newSelector: string | null): void {
    if (previousSelector && previousSelector !== 'walkThroughAlwaysHighlight') {
      const element = document.querySelector(previousSelector);
      element && this.renderer.removeClass(element, 'tour-highlight');
    }

    if (newSelector) {
      const element = document.querySelector(newSelector) as HTMLElement;
      element && this.renderer.addClass(element, 'tour-highlight');
    }
  }

  private calculateStepsAlignment(): void {
    for (let i = 0; i < this.steps.length; i++) {
      this.steps[i].align = this.calculateStepAlignment(i);
      this.steps[i].selector = this.calculateSelector(i);
    }

    this.stepsUpdated$.next();
  }

  private calculateSelector(stepNumber: number): string {
    const isMobile = this.appService.isMobile$.value;

    switch (stepNumber) {
      case 0:
        return '#walkThroughAlwaysHighlight';
      case 1:
        return '#walkThroughStep2';
      case 2:
        return '#walkThroughStep3';
      case 3:
        return isMobile ? '#tab-button-home' : '#walkThroughStep4Desktop';
      case 4:
        return isMobile ? '#tab-button-explore' : '#walkThroughStep5Desktop';
      case 5:
        return isMobile ? '#tab-button-saved' : '#walkThroughStep6Desktop';
      case 6:
        return isMobile ? '#tab-button-dashboard' : '#walkThroughStep7Desktop';
      case 7:
        return isMobile ? '#tab-button-menu' : '#walkThroughStep8Desktop';
      case 8:
        return '#walkThroughAlwaysHighlight';
      default:
        return '';
    }
  }

  private calculateStepAlignment(stepNumber: number): ProductTourAlign {
    const isMobile = this.appService.isMobile$.value;
    let desktopWidthToAdd = 0;
    if (window.innerWidth > 1140) {
      // See .app-content-max-width
      desktopWidthToAdd = (window.innerWidth - 1140) / 2;
    }

    switch (stepNumber) {
      case 0:
        return isMobile ? { top: 85 + this.statusBarService.safeAreaInPx + 'px', right: '10px' } : { top: '110px', right: 15 + desktopWidthToAdd + 'px' };
      case 1:
        return isMobile ? { top: 'auto', right: '60px' } : { top: 'auto', right: 60 + desktopWidthToAdd + 'px' };
      case 2:
        return isMobile ? { top: 'auto', right: '10px' } : { top: 'auto', right: 15 + desktopWidthToAdd + 'px' };
      case 3:
        return isMobile ? { bottom: '25px', left: '20px' } : { top: '110px', left: 60 + desktopWidthToAdd + 'px' };
      case 4:
        return isMobile ? { bottom: '25px', left: '40px' } : { top: '110px', left: 160 + desktopWidthToAdd + 'px' };
      case 5:
        return isMobile ? { bottom: '25px', left: '60px' } : { top: '110px', left: 260 + desktopWidthToAdd + 'px' };
      case 6:
        return isMobile ? { bottom: '25px', right: '40px' } : { top: '110px', left: 360 + desktopWidthToAdd + 'px' };
      case 7:
        return isMobile ? { bottom: '25px', right: '20px' } : { top: '110px', left: 20 + desktopWidthToAdd + 'px' };
      case 8:
        return isMobile ? { top: 85 + this.statusBarService.safeAreaInPx + 'px', right: '10px' } : { top: '110px', right: 15 + desktopWidthToAdd + 'px' };
      default:
        return { top: this.statusBarService.safeAreaInPx + 'px', left: '0px' };
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
